import { Facebook, Instagram, Linkedin, MapPin, Phone, Mail, Globe, ArrowRight, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, MaskedLines, SectionLabel } from "./common";
import LeadForm from "./LeadForm";
import { resolveMedia } from "../../lib/api";
import { trackContact } from "../../lib/metaPixel";

/* FREE GROWTH CHECK */
export function GrowthCheck({ data }) {
  const d = data || {};
  return (
    <section id="growth-check" data-testid="section-growth-check" className="relative overflow-hidden bg-navy py-24 md:py-32">
      <div className="absolute -right-20 top-1/2 h-96 w-96 -translate-y-1/2 rounded-full bg-[#FF5722]/10 blur-3xl pointer-events-none" />
      <div className="relative mx-auto max-w-6xl px-5 sm:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <SectionLabel dark>Free Growth Check</SectionLabel>
            <h2 className="font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-6xl">{d.heading}</h2>
            <p className="mt-6 max-w-xl font-body text-lg leading-relaxed text-white/70">{d.copy}</p>
            <p className="mt-6 font-body text-sm font-semibold uppercase tracking-wide text-[#FF5722]">{d.note}</p>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-3xl border border-white/10 bg-white/5 p-8 text-white backdrop-blur-md">
              <span className="font-display text-xs font-bold uppercase tracking-widest text-[#FF5722] mb-3 block">
                Complimentary Diagnostic
              </span>
              <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">
                Ready to review your marketing foundation?
              </h3>
              <p className="mt-3 font-body text-sm leading-relaxed text-white/70">
                Share your current business targets with us. We will audit your positioning, discoverability, and conversion gaps with zero obligation.
              </p>
              <div className="mt-8 flex flex-col gap-3">
                <a
                  href="#contact"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FF5722] px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-[#E64A19]"
                >
                  {d.cta || "Request Growth Consultation"} <ArrowRight size={16} />
                </a>
                <a
                  href="https://wa.me/919322106632"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackContact("whatsapp", { location: "growth_check", label: "Instant WhatsApp Connect" })}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-emerald-500/50 bg-emerald-500/10 px-6 py-3 font-display text-xs font-bold uppercase tracking-wider text-emerald-300 transition hover:bg-emerald-500 hover:text-white"
                >
                  <MessageCircle size={15} /> Instant WhatsApp Connect
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* CLIENTS / PARTNERS */
export function Clients({ data }) {
  const d = data || {};
  return (
    <section id="clients" data-testid="section-clients" className="bg-white py-20 md:py-28">
      <div className="mx-auto max-w-6xl px-5 text-center sm:px-8">
        <SectionLabel className="justify-center">Reach</SectionLabel>
        <Reveal>
          <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-navy sm:text-5xl">{d.heading}</h2>
          <p className="mt-4 font-display text-lg font-bold uppercase tracking-[0.2em] text-[#FF5722]">{d.sub}</p>
        </Reveal>
      </div>
    </section>
  );
}

/* CONTACT */
export function Contact({ data, settings }) {
  const d = data || {};
  const s = settings || {};
  return (
    <section id="contact" data-testid="section-contact" className="bg-[#F8F9FA] py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionLabel>Contact</SectionLabel>
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <h2 className="font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-navy sm:text-5xl lg:text-6xl">
              <MaskedLines lines={d.heading_lines || []} lineClass="block" />
            </h2>
            <p className="mt-6 max-w-md font-body text-lg leading-relaxed text-navy/60">{d.copy}</p>
            <div className="mt-10 space-y-4">
              <ContactRow icon={<Phone size={18} />} label={s.phone} href={`tel:${s.phone_raw}`} testId="contact-phone" />
              <ContactRow icon={<Mail size={18} />} label={s.email} href={`mailto:${s.email}`} testId="contact-email" />
              <ContactRow icon={<MapPin size={18} />} label={s.location} testId="contact-location" />
              <ContactRow icon={<Globe size={18} />} label={s.website} href={`https://${s.website}`} testId="contact-website" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-3xl border border-navy/10 bg-white p-6 sm:p-10 shadow-sm">
              <LeadForm source="contact" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon, label, href, testId }) {
  const content = (
    <div className="flex items-center gap-4">
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#FF5722]/10 text-[#FF5722]">{icon}</span>
      <span className="font-body text-base font-medium text-navy">{label}</span>
    </div>
  );
  const handleClick = () => {
    if (href?.startsWith("tel:")) {
      trackContact("phone", { location: "contact_section", label });
    } else if (href?.startsWith("mailto:")) {
      trackContact("email", { location: "contact_section", label });
    }
  };
  return href ? (
    <a href={href} data-testid={testId} onClick={handleClick} className="block transition hover:translate-x-1">{content}</a>
  ) : (
    <div data-testid={testId}>{content}</div>
  );
}

/* FOOTER */
export function Footer({ content }) {
  const c = content || {};
  const s = c.settings || {};
  const f = c.footer || {};
  const year = new Date().getFullYear();
  const links = [
    { label: "Home", href: "/", isRoute: true },
    { label: "About ADMALL", href: "/about", isRoute: true },
    { label: "Services", href: "/services", isRoute: true },
    { label: "Industries", href: "/industries", isRoute: true },
    { label: "Work & Portfolio", href: "/work", isRoute: true },
    { label: "Contact", href: "/contact", isRoute: true },
  ];

  return (
    <footer id="footer" data-testid="section-footer" className="bg-navy-dark pb-28 pt-20 lg:pb-16">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Link to="/">
              <img src={resolveMedia(c.logo_url || "/admall-logo.png")} alt="ADMALL INDIA" className="mb-6 h-12 w-auto bg-white/95 rounded-lg p-1.5" />
            </Link>
            <p className="font-display text-2xl font-extrabold uppercase tracking-tight text-white sm:text-3xl">{f.tagline}</p>
            <p className="mt-4 max-w-md font-body text-sm text-white/50">{f.disciplines}</p>
          </div>
          <div className="lg:col-span-4">
            <p className="mb-4 font-display text-sm font-bold uppercase tracking-widest text-[#FF5722]">Navigate</p>
            <div className="grid grid-cols-2 gap-2">
              {links.map((l) =>
                l.isRoute ? (
                  <Link
                    key={l.label}
                    to={l.href}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="font-body text-sm text-white/60 transition hover:text-white"
                  >
                    {l.label}
                  </Link>
                ) : (
                  <a
                    key={l.label}
                    href={l.href}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="font-body text-sm text-white/60 transition hover:text-white"
                  >
                    {l.label}
                  </a>
                )
              )}
            </div>
          </div>
          <div className="lg:col-span-3">
            <p className="mb-4 font-display text-sm font-bold uppercase tracking-widest text-[#FF5722]">Connect</p>
            <p className="font-body text-sm text-white/60">{s.phone}</p>
            <p className="font-body text-sm text-white/60">{s.email}</p>
            <p className="mt-2 font-body text-sm text-white/60">{s.location}</p>
            <div className="mt-5 flex gap-3">
              <Social href={s.facebook} testId="footer-facebook"><Facebook size={18} /></Social>
              <Social href={s.instagram} testId="footer-instagram"><Instagram size={18} /></Social>
              <Social href={s.linkedin} testId="footer-linkedin"><Linkedin size={18} /></Social>
            </div>
          </div>
        </div>
        <div className="mt-14 flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-6 sm:flex-row">
          <p className="font-body text-xs text-white/40">© {year} ADMALL INDIA. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <button
              type="button"
              data-testid="footer-cookie-settings-btn"
              onClick={() => window.dispatchEvent(new CustomEvent("admall_open_cookie_settings"))}
              className="font-body text-xs text-white/40 transition hover:text-[#FF5722]"
            >
              Cookie Settings
            </button>
            <a href="/admin" data-testid="footer-admin-link" className="font-body text-xs text-white/40 transition hover:text-[#FF5722]">Admin</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

function Social({ href, children, testId }) {
  return (
    <a href={href || "#"} target="_blank" rel="noopener noreferrer" data-testid={testId}
      className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/70 transition hover:border-[#FF5722] hover:bg-[#FF5722] hover:text-white">
      {children}
    </a>
  );
}
